<script lang="ts">
  import type { EventClassificationChoice } from '../lib/chart-model';
  import type { ParameterDefinition, PlotDefinition, PlotSpec } from '../lib/types';

  export let plot: PlotSpec;
  export let parameter: ParameterDefinition;
  export let definition: PlotDefinition;
  export let selectedEventVisible: boolean;
  export let eventClassificationChoices: EventClassificationChoice[];
  export let hiddenEventClassificationKeys: Set<string>;
  export let visibleEventClassificationCount: number;
  export let onSetOption: (name: string, enabled: boolean) => void;
  export let onSetEventClassificationVisible: (key: string, visible: boolean) => void;
  export let onShowAllEventClassifications: () => void;
  export let onHideAllEventClassifications: () => void;

  $: showNeutralControl = ((plot.kind === 'phase-series' || plot.kind === 'day-polar') && parameter.phaseColumns?.length === 4) || selectedEventVisible;
  $: hasHeaderOptions = definition.supportsEvents || definition.supportsHistogram || showNeutralControl;

  function eventClassificationCountId(key: string): string {
    return `event-classification-count-${plot.id}-${key}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  function closeEventClassificationFilterOnClickAway(node: HTMLDetailsElement) {
    const closeIfOutside = (event: MouseEvent) => {
      if (node.open && !event.composedPath().includes(node)) node.open = false;
    };
    document.addEventListener('click', closeIfOutside, true);
    return {
      destroy() {
        document.removeEventListener('click', closeIfOutside, true);
      }
    };
  }
</script>

{#if hasHeaderOptions}
<div class="plot-panel__header-options" role="group" aria-label="Plot display options">
  {#if definition.supportsEvents}
    <label><input type="checkbox" checked={plot.options.showEvents} on:change={(event) => onSetOption('showEvents', (event.currentTarget as HTMLInputElement).checked)} /> Show events</label>
    {#if plot.options.showEvents && eventClassificationChoices.length}
      <details class="event-classification-filter" use:closeEventClassificationFilterOnClickAway>
        <summary>
          <span>Classifications</span>
          <span
            class="event-classification-filter__count"
            aria-label={`${visibleEventClassificationCount} of ${eventClassificationChoices.length} classifications shown`}
          >{visibleEventClassificationCount}/{eventClassificationChoices.length}</span>
        </summary>
        <div class="event-classification-filter__menu">
          <fieldset>
            <legend class="sr-only">Event classifications shown on this plot</legend>
            {#each eventClassificationChoices as classification (classification.key)}
              <label>
                <input
                  type="checkbox"
                  checked={!hiddenEventClassificationKeys.has(classification.key)}
                  aria-label={classification.label}
                  aria-describedby={eventClassificationCountId(classification.key)}
                  on:change={(event) => onSetEventClassificationVisible(classification.key, (event.currentTarget as HTMLInputElement).checked)}
                />
                <span class="event-classification-filter__option-label">{classification.label}</span>
                <span id={eventClassificationCountId(classification.key)} class="event-classification-filter__option-count">
                  <span aria-hidden="true">{classification.count}</span>
                  <span class="sr-only">{classification.count} {classification.count === 1 ? 'event' : 'events'} in the current range</span>
                </span>
              </label>
            {/each}
          </fieldset>
          <div class="event-classification-filter__actions">
            <button class="button button--quiet" type="button" on:click={onShowAllEventClassifications} disabled={visibleEventClassificationCount === eventClassificationChoices.length}>Show all</button>
            <button class="button button--quiet" type="button" on:click={onHideAllEventClassifications} disabled={visibleEventClassificationCount === 0}>Hide all</button>
          </div>
        </div>
      </details>
    {/if}
  {/if}
  {#if definition.supportsHistogram}
    <label><input type="checkbox" checked={plot.options.showHistogram} on:change={(event) => onSetOption('showHistogram', (event.currentTarget as HTMLInputElement).checked)} /> Show histogram</label>
    {#if plot.options.showHistogram}<label><input type="checkbox" checked={plot.options.cumulative} on:change={(event) => onSetOption('cumulative', (event.currentTarget as HTMLInputElement).checked)} /> Cumulative</label>{/if}
  {/if}
  {#if showNeutralControl}
    <label><input type="checkbox" checked={plot.options.showNeutral} on:change={(event) => onSetOption('showNeutral', (event.currentTarget as HTMLInputElement).checked)} /> Show neutral</label>
  {/if}
</div>
{/if}
